import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, MapPin, Plus, Trash2, X } from 'lucide-react';
import { employeeService } from '../../services/employeeService';

export default function EmployeeContactsCard({ employeeUuid, canEdit = true }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    contact_type: 'emergency',
    full_name: '',
    relationship: '',
    phone: '',
    email: '',
    address: '',
    priority: 1
  });

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await employeeService.getEmployeeContacts(employeeUuid);
      setContacts(res.data || []);
    } catch (err) {
      console.error('Failed to load employee contacts', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeUuid) fetchContacts();
  }, [employeeUuid]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await employeeService.addEmployeeContact(employeeUuid, formData);
      setIsModalOpen(false);
      fetchContacts();
      setFormData({
        contact_type: 'emergency',
        full_name: '',
        relationship: '',
        phone: '',
        email: '',
        address: '',
        priority: 1
      });
    } catch (err) {
      console.error('Error saving contact:', err);
      alert('Failed to save contact.');
    }
  };

  const handleDelete = async (contactUuid) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await employeeService.deleteEmployeeContact(employeeUuid, contactUuid);
      fetchContacts();
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Failed to delete contact.');
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 flex flex-col gap-4">
      
      {/* Card Header matching theme */}
      <div className="flex items-center justify-between border-b pb-2 mb-2">
        <h3 className="text-lg font-semibold text-gray-800">Emergency & Additional Contacts</h3>
        
        {canEdit === true && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition cursor-pointer shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add Contact
          </button>
        )}
      </div>

      {/* Contacts List Grid */}
      {loading ? (
        <p className="text-xs text-gray-500 text-center py-4">Loading contacts...</p>
      ) : contacts.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4">No emergency contacts added yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contacts.map((contact) => (
            <div key={contact.uuid} className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex flex-col justify-between gap-3 shadow-2xs relative group">
              
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                        {contact.full_name}
                      </h4>
                      <p className="text-[11px] text-gray-500 font-medium">
                        {contact.relationship} • <span className="capitalize text-blue-600 font-semibold">{contact.contact_type}</span>
                      </p>
                    </div>
                  </div>

                  {canEdit === true && (
                    <button 
                      onClick={() => handleDelete(contact.uuid)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition cursor-pointer"
                      title="Delete Contact"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="mt-3 space-y-1.5 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="font-mono text-gray-800">{contact.phone}</span>
                  </div>
                  {contact.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="text-gray-800">{contact.email}</span>
                    </div>
                  )}
                  {contact.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                      <span className="text-gray-800">{contact.address}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-200/60 text-[10px] text-gray-400">
                <span>Priority: {contact.priority}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Contact Modal */}
      {isModalOpen && canEdit === true && (
        <div className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-gray-200 overflow-hidden animate-in fade-in duration-200">
            
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h4 className="text-sm font-bold text-gray-900">Add New Contact</h4>
              <button onClick={() => setIsModalOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-3">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Contact Type</label>
                <select 
                  name="contact_type" 
                  value={formData.contact_type} 
                  onChange={handleChange}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600 bg-white capitalize"
                >
                  <option value="emergency">Emergency</option>
                  <option value="parent">Parent</option>
                  <option value="spouse">Spouse</option>
                  <option value="sibling">Sibling</option>
                  <option value="guardian">Guardian</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Full Name</label>
                <input 
                  type="text" 
                  name="full_name" 
                  placeholder="Enter full name" 
                  value={formData.full_name} 
                  onChange={handleChange}
                  required
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Relationship</label>
                  <input 
                    type="text" 
                    name="relationship" 
                    placeholder="e.g. Father, Brother" 
                    value={formData.relationship} 
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Priority</label>
                  <input 
                    type="number" 
                    name="priority" 
                    min="1" 
                    value={formData.priority} 
                    onChange={handleChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Phone Number</label>
                <input 
                  type="text" 
                  name="phone" 
                  placeholder="+91-9898989898" 
                  value={formData.phone} 
                  onChange={handleChange}
                  required
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Email Address</label>
                <input 
                  type="email" 
                  name="email" 
                  placeholder="example@gmail.com" 
                  value={formData.email} 
                  onChange={handleChange}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase">Address</label>
                <input 
                  type="text" 
                  name="address" 
                  placeholder="City, State, Country" 
                  value={formData.address} 
                  onChange={handleChange}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-800 focus:outline-blue-600"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-sm cursor-pointer"
                >
                  Save Contact
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

    </div>
  );
}